/** @jsxImportSource react */
import { retrieveLocalStorage } from '@common/helpers/localStorage';
import { phoneFormatter, phoneParser } from '@common/helpers/phone';
import clsxm from '@common/lib/clsxm';
import useValidateFormSubmission from '@react/cta/hooks/useValidateFormSubmission';
import FieldAdapter from '@react/form/components/FieldAdapter';
import SubmitButton from '@react/form/components/SubmitButton';
import {
  composeValidators,
  email,
  required,
  usPhone,
} from '@react/form/helpers/validation';
import TosAndPrivacy from '@react/shared/components/TosAndPrivacy';
import { BaseImage } from '@react/system';
import { getClientData } from '@sly/core/constants/localStorage';
import { DEFAULT_EMAIL_VALUE } from '@sly/core/constants/retentionPopup';
import Spinner from '@sly/frontend/react/shared/components/Spinner';
import { bool, func, object, string } from 'proptypes';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';

function ContactFields({ user, shrinkContact, isReadOnlyEmail }) {
  if ((!user || (!user?.phoneNumber && !user?.email)) && shrinkContact) {
    return (
      <div className='flex gap-m child:flex-1'>
        <Field
          name='phone'
          label='Phone'
          type='text'
          parse={phoneParser}
          format={phoneFormatter}
          component={FieldAdapter}
          validate={composeValidators(required, usPhone)}
          data-tp-id='Field-phone'
        />
        <Field
          name='email'
          label='Email'
          type='email'
          component={FieldAdapter}
          validate={composeValidators(required, email)}
          data-tp-id='Field-email'
          readOnly={isReadOnlyEmail}
        />
      </div>
    );
  }
  return (
    <div className='flex gap-m child:flex-1'>
      {!(user && user.phoneNumber) && (
        <Field
          name='phone'
          label='Phone'
          type='text'
          parse={phoneParser}
          format={phoneFormatter}
          component={FieldAdapter}
          validate={composeValidators(required, usPhone)}
          data-tp-id='Field-phone'
        />
      )}
      {!(user && user.email) && (
        <Field
          name='email'
          label='Email'
          type='email'
          component={FieldAdapter}
          validate={composeValidators(required, email)}
          data-tp-id='Field-email'
          readOnly={isReadOnlyEmail}
        />
      )}
    </div>
  );
}

const TalkToAgentForm = ({
  handleSubmit,
  description,
  user,
  hasLocation,
  agentFirstName,
  messagePrompt,
  showMessageFieldFirst,
  hideMessage,
  image,
  messagePlaceholder,
  buttonLabel,
  shrinkContact,
  initialMessage,
}) => {
  const messageLabel =
    messagePrompt === ''
      ? `What can ${agentFirstName} help you with?`
      : messagePrompt;
  const submitButtonLabel = buttonLabel === '' ? 'Send message' : buttonLabel;
  const showDesc = description !== '';
  const showTos = !user;
  const emailFromStorage = !user
    ? retrieveLocalStorage(DEFAULT_EMAIL_VALUE)
    : '';

  const [isReadOnlyEmail, setIsReadOnlyEmail] = useState(false);
  useEffect(() => {
    setIsReadOnlyEmail(!!emailFromStorage);
  }, [emailFromStorage]);

  const handleSubmitWithValidation = useValidateFormSubmission({
    onSubmit: handleSubmit,
  });

  const messageField = (
    <Field
      type='textarea'
      rows='3'
      name='message'
      label={messageLabel}
      placeholder={messagePlaceholder}
      component={FieldAdapter}
      validate={required}
      data-tp-id='Field-message'
    />
  );

  const clientData = getClientData();

  const initialValues = {
    firstName: clientData?.firstName,
    lastName: clientData?.lastName,
    phone: clientData?.phone,
    email: clientData?.email,
  };

  if (emailFromStorage) {
    initialValues['email'] = emailFromStorage;
  }

  if (initialMessage) {
    initialValues['message'] = initialMessage;
  }

  return (
    <section className='p-l'>
      {image && (
        <div
          className='flex items-center justify-center'
          data-tp-id='gen-div-03e46853-b051-4526-b08d-8cff93e1c83d'
        >
          <BaseImage
            width='1'
            height='1'
            layout='fill'
            src={image}
            data-tp-id='gen-Image-15dc8218-1a3f-4a37-9628-e9755bf254a9'
            alt='Agent profile picture'
          />
        </div>
      )}
      {showDesc && description && (
        <div
          className='mt-m mb-xl'
          data-tp-id='gen-div-9a90dbab-62d4-432e-92c9-4c3b7b0c80f1'
        >
          {description}
        </div>
      )}
      <Form
        onSubmit={handleSubmitWithValidation}
        keepDirtyOnReinitialize
        data-tp-id='gen-Form-c68c3b1c-b8fc-4d99-bba5-f802f0fd691c'
        initialValues={initialValues}
      >
        {({ handleSubmit: innerHandleSubmit, submitting, invalid }) => {
          return (
            <form
              onSubmit={innerHandleSubmit}
              data-tp-id='gen-Form-talkToAnAgent'
            >
              {showMessageFieldFirst && !hideMessage && messageField}
              {hasLocation && (
                <Field
                  name='location'
                  label='Where are you searching for homes?'
                  type='locationSearch'
                  placeholder='Search By City, State'
                  component={FieldAdapter}
                  required
                  data-tp-id='Field-location'
                />
              )}
              {!(user && user.name) && (
                <div
                  className='flex gap-m child:flex-1'
                  data-tp-id='gen-div-ff747b2f-76df-4471-a4ad-a28e3849d1c2'
                >
                  <Field
                    name='firstName'
                    label='First Name'
                    type='text'
                    validate={required}
                    component={FieldAdapter}
                    data-tp-id='Field-firstName'
                  />
                  <Field
                    name='lastName'
                    label='Last Name'
                    type='text'
                    component={FieldAdapter}
                    validate={required}
                    data-tp-id='Field-lastName'
                  />
                </div>
              )}
              <ContactFields
                user={user}
                shrinkContact={shrinkContact}
                isReadOnlyEmail={isReadOnlyEmail}
              />
              {!showMessageFieldFirst && !hideMessage && messageField}
              <SubmitButton
                className={clsxm('mt-m mb-m w-full')}
                type='submit'
                data-tp-id='Button-submit'
                disabled={invalid || submitting}
              >
                {submitting && <Spinner />}
                {submitting ? 'Submitting' : submitButtonLabel}
              </SubmitButton>
              {showTos && <TosAndPrivacy className='text-center' />}
            </form>
          );
        }}
      </Form>
    </section>
  );
};

TalkToAgentForm.propTypes = {
  handleSubmit: func.isRequired,
  invalid: bool,
  submitting: bool,
  error: string,
  change: func,
  onLocationChange: func,
  heading: string.isRequired,
  description: string,
  user: object,
  hasLocation: bool,
  image: string,
  agentFirstName: string.isRequired,
  messagePrompt: string,
  showMessageFieldFirst: bool,
  hideMessage: bool,
  buttonKind: string,
  messagePlaceholder: string,
  buttonLabel: string,
  shrinkContact: bool,
};

TalkToAgentForm.defaultProps = {
  heading: 'Talk to a Seniorly Local Advisor',
  agentFirstName: 'we',
  messagePrompt: '',
  buttonKind: 'jumbo',
  messagePlaceholder: 'Type your question here. ',
  hideMessage: false,
  buttonLabel: '',
  shrinkContact: false,
};

export default TalkToAgentForm;
